import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { GraduationCap, School, ArrowRight, Loader2, Check, Minus } from 'lucide-react';

const INDIVIDUAL_FEATURES = [
  'Your own classes and pupils',
  'Full AI assessment workflow',
  'Progress tracking and reports',
  'Set up in under 2 minutes',
];

const SCHOOL_FEATURES = [
  'School-wide pupil records',
  'Invite and manage teachers',
  'Admin visibility of all assessments',
  'Key stage and year group management',
];

// Feature matrix: [label, individual, school]
const COMPARISON_ROWS: [string, boolean, boolean][] = [
  ['AI writing assessment (GPT-6 Luna)', true, true],
  ['Accept / Override grade', true, true],
  ['Progress charts and report comments', true, true],
  ['CSV pupil import', true, true],
  ['Multi-page writing upload', true, true],
  ['Anonymisation options', true, true],
  ['Invite and manage teachers', false, true],
  ['School-wide admin assessment view', false, true],
  ['Key stage / year group management', false, true],
];

export default function ChoosePlan() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [choosing, setChoosing] = useState<'individual' | 'school' | null>(null);

  const handleChoose = async (plan: 'individual' | 'school') => {
    setChoosing(plan);
    const { error } = await supabase.auth.updateUser({
      data: { plan_type: plan },
    });
    if (error) {
      toast({
        title: 'Something went wrong',
        description: error.message,
        variant: 'destructive',
      });
      setChoosing(null);
      return;
    }
    // Navigate back — Dashboard will re-evaluate with the new plan_type
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-3xl space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">How will you use WriteAssess?</h1>
          <p className="text-muted-foreground">
            Choose the setup that fits your situation — you can always contact us to change later.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* Individual card */}
          <Card className="shadow-soft cursor-pointer border-2 hover:border-primary transition-colors"
            onClick={() => !choosing && handleChoose('individual')}>
            <CardHeader className="pb-3">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
                <GraduationCap className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-xl">Individual Teacher</CardTitle>
              <CardDescription>
                Perfect for tutors and classroom teachers working independently.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2">
                {INDIVIDUAL_FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-primary shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                className="w-full"
                disabled={!!choosing}
                onClick={(e) => { e.stopPropagation(); handleChoose('individual'); }}
              >
                {choosing === 'individual' ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="mr-2 h-4 w-4" />
                )}
                Start as Individual
              </Button>
              <p className="text-xs text-center text-muted-foreground">Free plan included — upgrade to paid anytime after setup.</p>
            </CardContent>
          </Card>

          {/* School card */}
          <Card className="shadow-soft cursor-pointer border-2 hover:border-primary transition-colors"
            onClick={() => !choosing && handleChoose('school')}>
            <CardHeader className="pb-3">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
                <School className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-xl">School Plan</CardTitle>
              <CardDescription>
                For school leaders setting up WriteAssess for their whole staff.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2">
                {SCHOOL_FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-primary shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                className="w-full"
                variant="outline"
                disabled={!!choosing}
                onClick={(e) => { e.stopPropagation(); handleChoose('school'); }}
              >
                {choosing === 'school' ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="mr-2 h-4 w-4" />
                )}
                Set Up My School
              </Button>
              <p className="text-xs text-center text-muted-foreground">After school setup you'll select your pricing tier and make payment.</p>
            </CardContent>
          </Card>
        </div>

        {/* Feature comparison table */}
        <div className="bg-card rounded-2xl border shadow-soft overflow-hidden">
          <div className="grid grid-cols-3 text-sm font-semibold border-b bg-muted/40">
            <div className="p-4 text-muted-foreground">Feature</div>
            <div className="p-4 text-center border-l">
              <span className="inline-flex items-center gap-1.5">
                <GraduationCap className="h-4 w-4" /> Individual
              </span>
            </div>
            <div className="p-4 text-center border-l">
              <span className="inline-flex items-center gap-1.5">
                <School className="h-4 w-4" /> School
              </span>
            </div>
          </div>
          {COMPARISON_ROWS.map(([label, individual, school], i) => (
            <div key={label} className={`grid grid-cols-3 text-sm ${i % 2 === 0 ? '' : 'bg-muted/20'} border-b last:border-b-0`}>
              <div className="p-4 text-muted-foreground">{label}</div>
              <div className="p-4 flex justify-center items-center border-l">
                {individual
                  ? <Check className="h-4 w-4 text-primary" />
                  : <Minus className="h-4 w-4 text-muted-foreground/30" />}
              </div>
              <div className="p-4 flex justify-center items-center border-l">
                {school
                  ? <Check className="h-4 w-4 text-primary" />
                  : <Minus className="h-4 w-4 text-muted-foreground/30" />}
              </div>
            </div>
          ))}
          <div className="p-4 text-center text-xs text-muted-foreground border-t bg-muted/20">
            Both plans are <strong>free during the beta</strong>. Pricing will be announced with plenty of notice.
          </div>
        </div>
      </div>
    </div>
  );
}
